
import {defineComponent, PropType} from 'vue'
import {isOfferApiResponseSuccess, OfferService} from "../../services/OfferService";
import {OfferApiResponse, OfferApiResponseSuccess} from "../../models/OfferApiResponse";
import type {OfferPageDto} from "../../models/OfferPageDto";
import SkeletonOfferDetailPage from "../../components/SkeletonOfferDetailPage.vue";
import Lien from "../../../common/components/ob1/Lien.vue";
import CommercialTag from "../../../common/components/ob1/CommercialTag.vue";
import Price from "../../../common/components/ob1/Price.vue";
import Breadcrumb from "../../../common/components/ob1/Breadcrumb.vue";
import LinkFoldUnFold from "../../../common/components/ob1/LinkFoldUnfold.vue";
import OptionEditoPopin from "../../../common/components/vue/OptionEditoPopin.vue";
import ThreeColumnsEditoPopin from "../../../common/components/vue/ThreeColumnsEditoPopin.vue";
import EditoPopin from "../../../common/components/vue/EditoPopin.vue";
import LegalMentions from "../../components/LegalMentions.vue";
import ModalDialog from "../../../common/components/ob1/ModalDialog.vue";
import ShowcaseFaq from "../../../orange/broadband/demands/showcase/internetandmobileoffers/components/ShowcaseFaq.vue";
import Bouton from "../../../common/components/ob1/Bouton.vue";
import {CartService} from "../../services/CartService";
import {OfferCartRequest, OfferCartResponseSuccess, OfferUniverse} from "../../models/CartApiResponse";
import ModalAfterAddToCart from "../../../common/components/custom/ModalAfterAddToCart.vue";
import DiscountInfoBanner from "../../../fmfmo/common/components/DiscountInfoBanner.vue";
import EditorialPush from "../../../common/components/ob1/EditorialPush.vue";

export default defineComponent({
    name: "OfferPage",
    components: {
        DiscountInfoBanner,
        ModalAfterAddToCart,
        EditorialPush,
        Bouton,
        ShowcaseFaq,
        ModalDialog,
        OptionEditoPopin, ThreeColumnsEditoPopin, EditoPopin,
        LinkFoldUnFold,
        Breadcrumb,
        LegalMentions, Price, CommercialTag, Lien, SkeletonOfferDetailPage
    },
    props: {
        dto: {
            type: Object as PropType<OfferPageDto>,
            required: true
        },
    },
    data() {
        return {
            offerApiResponse: null as OfferApiResponseSuccess | null,
            editoPopinRefs: [] as EditoPopin[],
        }
    },
    mounted() {
        OfferService.getOffer(
            this.dto.seoId,
            this.dto.deviceSeoId ?? null,
            this.dto.showcaseContext,
            this.dto.offerType
        ).then((response: OfferApiResponse) => {
            if (isOfferApiResponseSuccess(response)) {
                this.offerApiResponse = response;

                this.addElementsForModalToggle();

                //update des données META de la page
                const descEl = document.querySelector('head meta[name="description"]');
                const titleEl = document.querySelector('head title');
                descEl?.setAttribute('content', this.offerApiResponse?.offer?.metaDescription);
                if (titleEl) {
                    titleEl.textContent = this.offerApiResponse?.offer?.metaTitle;
                }
            }
        });
    },
    updated() {
        this.addElementsForModalToggle();
    },
    computed: {
        ctaIsButton(): boolean {
            return !this.offerApiResponse?.buttonCta || Object.keys(this.offerApiResponse.formParams).length > 0;
        },
    },
    methods: {
        addElementsForModalToggle() {
            const offerDetails = this.$refs.offerDetails
            if (offerDetails) {
                // Rééecriture des liens dans la div contenant les détails de l'offre
                offerDetails.querySelectorAll("a").forEach((link: HTMLAnchorElement) => {
                    this.rewriteAnchor(link);
                });
            }

            // Masquer les boutons des différentes modals
            const threeColumnsEditoPopin = this.$refs.threeColumnsEditoPopin;
            if (threeColumnsEditoPopin && threeColumnsEditoPopin.$el) {
                threeColumnsEditoPopin.$el.querySelectorAll("[data-bs-target]")
                    .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
            }
            const optionEditoPopin = this.$refs.optionEditoPopin;
            if (optionEditoPopin && optionEditoPopin.$el) {
                optionEditoPopin.$el.querySelectorAll("[data-bs-target]")
                    .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
            }
            const editoPopin = this.$refs.editoPopin;
            if (editoPopin && editoPopin.$el) {
                editoPopin.$el.querySelectorAll("[data-bs-target]")
                    .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
            }
            this.editoPopinRefs.forEach((editoPopin: EditoPopin) => {
                if (editoPopin && editoPopin.$el) {
                    editoPopin.$el.querySelectorAll("[data-bs-target]")
                        .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
                }
            });
        },
        rewriteAnchor(link: HTMLAnchorElement) {
            const hrefAttr = link.getAttribute("href");
            if (!hrefAttr) return;

            if (hrefAttr.startsWith("#")) {
                link.target = "_self";
                link.dataset.bsToggle = "modal";
                link.dataset.oeventCategory = "detail_offre_modal";
                link.dataset.oeventLabel = toBroadbandTmsFormat(link.text);
                link.dataset.oeventAction = hrefAttr.slice(1);
                link.className = "ob1-link-arrow ob1-link-large";
            } else {
                link.target = "_blank";
                link.dataset.oeventCategory = "detail_offre_lien_externe";
                link.dataset.oeventLabel = toBroadbandTmsFormat(link.text);
                link.dataset.oeventAction = hrefAttr.slice(1);
            }
        },
        addEditoPopinRef(editoPopin: any, index: number, id: string): string {
            this.editoPopinRefs[index] = editoPopin as EditoPopin;
            return `editoPopin${id}`;
        },
        handleCtaClick(): void {
            if (this.ctaIsButton) {
                const buttons = ['topCtaButton', 'bottomCtaButton'];
                buttons.forEach(refName => this.$refs[refName].startAnimation());

                const cartRequest: OfferCartRequest = {
                    id: this.offerApiResponse.formParams['id'] || '',
                    device: this.offerApiResponse.formParams['device'] || null,
                    mobileId: this.offerApiResponse.formParams['mobileId'] || null,
                    universe: this.offerApiResponse.formParams['universe'] as OfferUniverse
                };

                CartService.setCart(cartRequest).then((response: OfferCartResponseSuccess) => {
                    buttons.forEach(refName => this.$refs[refName].stopAnimation());
                    if (this.offerApiResponse?.modalAfterAddToCart) {
                        this.$refs['modalAfterAddToCart'].show();
                    } else {
                        window.location.href = response.redirectUrl;
                    }
                }).catch(() => {
                    buttons.forEach(refName => this.$refs[refName].stopAnimation());
                });
            }
        }
    }
})
